import React, { createContext, useContext, useState, useEffect } from "react";
import io from "socket.io-client";
import { useAuth } from "@clerk/clerk-react";

const SocketContext = createContext();

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const { userId, isSignedIn, isLoaded } = useAuth();

  useEffect(() => {
    let socketUrl;
    if (process.env.REACT_APP_API_URL) {
      socketUrl = process.env.REACT_APP_API_URL.replace('/api', '');
    } else if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      socketUrl = 'http://localhost:5000';
    } else {
      socketUrl = 'https://bookmycinema.onrender.com';
    }

    console.log('🔌 Connecting to socket:', socketUrl);
    const socketInstance = io(socketUrl, {
      reconnection: true,
      reconnectionDelay: 500,
      reconnectionDelayMax: 2000,
      reconnectionAttempts: Infinity
    });

    socketInstance.on("connect", () => {
      console.log("✅ Socket connected:", socketInstance.id);
      setConnected(true);
    });

    socketInstance.on("disconnect", () => {
      console.log("⏹️ Socket disconnected");
      setConnected(false);
    });

    // High-Coverage Real-Time Sync Handlers
    const events = [
      "movie-added", "movie-updated", "movie-deleted", "movies-updated",
      "show-added", "show-updated", "show-deleted", "shows-updated",
      "theater-added", "theater-updated", "theater-deleted", "theaters-updated",
      "screen-added", "screen-updated", "screen-deleted",
      "seat-layout-updated", "partner-updated", "seats-released", "booking-made",
      "seats-locked", "seats-selected", "seat-conflict", "seats-available",
      "seats-booked", "seats-auto-released", "booking-status-updated",
      "booking-created", "booking-cancelled", "booking-deleted"
    ];

    events.forEach(eventName => {
      socketInstance.on(eventName, (data) => {
        console.log(`🛰️ Real-time: ${eventName}`, data);
        window.dispatchEvent(new CustomEvent(eventName, { detail: data }));

        // Legacy/Generic event mapping
        if (eventName.startsWith('movie')) window.dispatchEvent(new CustomEvent("movies-updated", { detail: data }));
        if (eventName.startsWith('theater')) window.dispatchEvent(new CustomEvent("theaters-updated", { detail: data }));
        if (eventName.startsWith('show')) window.dispatchEvent(new CustomEvent("shows-updated", { detail: data }));
      });
    });

    setSocket(socketInstance);
    return () => socketInstance.disconnect();
  }, []);

  // Robust Registration Logic
  useEffect(() => {
    if (!socket || !isLoaded) return;

    const registerIdentity = async () => {
      let identifier = null;
      if (isSignedIn && userId) {
        identifier = userId;
        console.log("🆔 Identifying as User:", userId);
      } else {
        identifier = localStorage.getItem("bookmycinema_guest_id");
        if (!identifier) {
          identifier = "guest_" + Math.random().toString(36).substring(2, 15);
          localStorage.setItem("bookmycinema_guest_id", identifier);
        }
        console.log("🆔 Identifying as Guest:", identifier);
      }

      if (identifier) {
        // Socket ID registration only
        socket.emit("register-user", identifier);
      }
    };

    registerIdentity();

    // Listen for reconnection to re-register
    socket.on("connect", registerIdentity);
    return () => {
      socket.off("connect", registerIdentity);
    };
  }, [socket, userId, isSignedIn, isLoaded]);

  return (
    <SocketContext.Provider value={{ socket, connected }}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
